import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import {
  FolderOpen, Library, Package, Settings, Plus, Upload, ClipboardCheck, Lightbulb, Clock,
} from "lucide-react";
import API from "@/lib/api";

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  useEffect(() => {
    if (open && projects.length === 0) {
      API.get("/projects").then(({ data }) => setProjects(data || [])).catch(() => {});
    }
  }, [open, projects.length]);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command, or search playbooks..." data-testid="cmdk-input" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Navigate">
          <CommandItem onSelect={() => go("/dashboard")} data-testid="cmdk-nav-projects">
            <FolderOpen className="mr-2 h-4 w-4" /><span>Playbooks</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/library")} data-testid="cmdk-nav-library">
            <Library className="mr-2 h-4 w-4" /><span>Library</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/packs")} data-testid="cmdk-nav-packs">
            <Package className="mr-2 h-4 w-4" /><span>Packs</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/settings")} data-testid="cmdk-nav-settings">
            <Settings className="mr-2 h-4 w-4" /><span>Settings</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Actions">
          <CommandItem onSelect={() => go("/library?action=import")} data-testid="cmdk-act-import">
            <Upload className="mr-2 h-4 w-4" /><span>Import Prompts</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/library?action=audit")} data-testid="cmdk-act-audit">
            <ClipboardCheck className="mr-2 h-4 w-4" /><span>Audit Library</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/library?action=suggest")} data-testid="cmdk-act-suggest">
            <Lightbulb className="mr-2 h-4 w-4" /><span>Suggest Additions</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/packs")} data-testid="cmdk-act-new-project">
            <Plus className="mr-2 h-4 w-4" /><span>Browse Packs</span>
          </CommandItem>
        </CommandGroup>

        {projects.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Recent Playbooks">
              {projects.slice(0, 8).map((p) => (
                <CommandItem key={p.id} onSelect={() => go(`/projects/${p.id}`)} data-testid={`cmdk-project-${p.id}`}>
                  <Clock className="mr-2 h-4 w-4 text-[var(--pf-text-muted)]" />
                  <span className="truncate">{p.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
